<template>
    <div
        :class="{
            [$style['login']]: true,
            [$style['login_scroll']]: isScroll,
            [$style['login_visual-constructor']]: visualConstructor && theme.toLowerCase() !== Theme.SIMLA,
        }"
        @scroll="onScroll"
    >
        <main
            ref="main"
            :class="{
                [$style['login__content']]: true,
                [$style['login__content_top']]: isTopPosition,
                [$style['login__content_retailcrm']]: theme.toLowerCase() === Theme.RETAILCRM,
            }"
        >
            <div :class="$style['top']">
                <UiButton
                    v-if="showBackToTheSystem"
                    appearance="tertiary"
                    @click="jumpToTheSystem"
                >
                    <IconArrowBackward /> {{ t('backToTheSystem') }}
                </UiButton>

                <UiButton
                    v-else-if="isAuthorized"
                    appearance="tertiary"
                    @click="toLogout"
                >
                    <IconArrowBackward /> {{ t('logout') }}
                </UiButton>
            </div>
            <div
                :class="{
                    [$style['login__form-wrap']]: true,
                    [$style['login__form-wrap_top']]: isTopPosition,
                }"
            >
                <div v-if="$slots.header" :class="$style['login__header']">
                    <slot name="header" />
                </div>

                <div :class="$style['login__logo']">
                    <img
                        :src="logo"
                        :alt="siteName"
                    />
                </div>

                <h1 :class="$style['login__title']">
                    <slot name="title">
                        {{ t("title", {siteName: siteName}) }}
                    </slot>
                </h1>
                <div :class="$style['login__description']">
                    <slot name="description" />
                </div>
                <div :class="$style['login__form']">
                    <slot />
                </div>
            </div>
        </main>

        <div
            v-if="theme.toLowerCase() === Theme.RETAILCRM"
            :class="$style['info-panel-wrapper']"
        >
            <div :class="$style['info-panel']">
                <div :class="$style['info-panel__content']">
                    <div :class="$style['info-panel__title']">
                        {{ !visualConstructor ? t("infoPanel.title.text") : t("visualConstructor.infoPanel.title") }}
                    </div>

                    <div v-if="!visualConstructor" :class="$style['info-panel__price']">
                        <span :class="$style['info-panel__price-new']">
                            {{ t("infoPanel.title.price") }}
                        </span>

                        <span :class="$style['info-panel__price-crossed']">
                            {{ t("infoPanel.title.priceCrossed") }}
                        </span>
                    </div>

                    <div v-else :class="$style['info-panel__description']">
                        {{ t("visualConstructor.infoPanel.description") }}
                    </div>

                    <UiButton
                        :class="$style['info-panel__button']"
                        :href="learnMoreLink"
                        target="_blank"
                        appearance="outlined"
                        size="md"
                    >
                        {{ t("infoPanel.learnMore") }}
                    </UiButton>
                </div>

                <div :class="$style['info-panel__image-wrapper']">
                    <img
                        :class="$style['info-panel__image']"
                        :src="preview.src"
                        :srcset="preview.srcset"
                        draggable="false"
                        loading="lazy"
                        alt=""
                    />
                </div>
            </div>
        </div>

        <img
            v-else
            :src="backgroundImageUrl"
            :class="$style['login__image']"
            draggable="false"
            loading="lazy"
            alt=""
        />
    </div>
</template>

<script lang="ts" setup>
import IconArrowBackward from '@omnica/icon/assets/sprites/arrows/arrow-backward.svg?component'

import { computed } from 'vue';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth';
import { useRoute } from 'vue-router';
import { useRouter } from 'vue-router';
import { useTheme } from '@/composables/theme';

import { LOCALES } from '@/i18n';
import { Theme } from '@/composables/theme';

import LoginImageRu from '@/assets/img/login__decor_ru.svg?url';
import LoginImageEn from '@/assets/img/login__decor_en.svg?url';
import LoginImageEs from '@/assets/img/login__decor_es.svg?url';

import SalesChannelsBackground1x from '@/assets/img/sales-channels-background@1x.webp'
import SalesChannelsBackground2x from '@/assets/img/sales-channels-background@2x.webp'
import VisualConstructorBackground1x from '@/assets/img/visual-constructor-background@1x.webp'
import VisualConstructorBackground2x from '@/assets/img/visual-constructor-background@2x.webp'

interface ScrollEvent {
  scrollTop: number;
  clientHeight: number;
  scrollHeight: number;
}

interface ImageSource {
  src: string,
  srcset: string,
}

defineProps({
    isTopPosition: {
        type: Boolean,
        default: false,
    },
    isScroll: {
        type: Boolean,
        default: false,
    },
})

const visualConstructor = true;

const emit = defineEmits<(e: 'scroll', data: ScrollEvent) => void>()

const { t, locale } = useI18n();
const { logo, siteName, theme } = useTheme();
const route = useRoute();
const router = useRouter();

const { isAuthorized } = useAuthStore();

const main = ref<HTMLElement | null>(null);

const preview = computed((): ImageSource => {
    return {
        src: !visualConstructor ? SalesChannelsBackground1x : VisualConstructorBackground1x,
        srcset: !visualConstructor ? `${SalesChannelsBackground1x} 1x, ${SalesChannelsBackground2x} 2x` : `${VisualConstructorBackground1x} 1x, ${VisualConstructorBackground2x} 2x`,
    }
})

const learnMoreLink = computed(() => {
    return !visualConstructor
        ? 'https://www.retailcrm.ru/crm/sales-channels?utm_source=retailcrm&utm_medium=banner_sales_channels&utm_campaign=offer_7sales_channels'
        : 'https://www.retailcrm.ru/marketing/canvas?utm_source=retailcrm&utm_medium=banner&utm_campaign=login_offer&utm_content=canvas_v1'
})

const backgroundImageUrl = computed(() => {
    switch (locale.value) {
        case LOCALES.EN:
            return LoginImageEn;
        case LOCALES.ES:
            return LoginImageEs;
        default:
            return LoginImageRu;
    }
});

const toLogout = () => router.push({ name: 'logout', query: route.query })

const onScroll = (): void => {
    if (!main.value) {
        return
    }

    emit('scroll', {
        scrollTop: main.value.scrollTop,
        clientHeight: main.value.clientHeight,
        scrollHeight: main.value.scrollHeight,
    })
}

const showBackToTheSystem = !!route.query.ref

const jumpToTheSystem = () => {
    if (!showBackToTheSystem) {
        return
    }

    location.href = String(route.query.ref)
}
</script>

<style lang="less" module>
@import (reference) "../../assets/styles/variables.less";
@import (reference) "@omnica/base/assets/stylesheets/layout.less";
@import (reference) "@omnica/base/assets/stylesheets/palette.less";
@import (reference) "@omnica/base/assets/stylesheets/typography.less";

.login {
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;
  height: 100%;
  background-color: #FFFFFF;
  color: @black-500;
  font-family: @font-family;
  line-height: 1.5;
  overflow-y: auto;

  &_scroll {
    overflow-y: scroll;
  }

  &__image {
    width: auto;
    min-width: 544px;
    height: 100%;
    object-fit: cover;
    object-position: left;
  }

  &__content {
    display: flex;
    flex: 1;
    flex-direction: column;
    height: 100%;
    padding: @spacing-xl;
    background-color: #fff;
    align-items: center;
    position: relative;

    &_top {
        align-items: flex-start;
    }
  }

  &__inner-wrap {
    height: 100%;
    position: relative;
  }

  &__logo {
    width: 56px;
    height: 56px;
    margin: 0 auto;
  }

  &__title {
    font: 800 40px/44px @font-family-Gilroy;
    text-align: center;
    margin-top: @spacing-m;
    margin-bottom: 0;
  }

  &__description {
    text-align: center;
    margin-top: 6px;
  }

  &__form {
    margin: @spacing-l auto 0;
    max-width: 352px;
  }

  &__form-wrap {
    width: 400px;
    margin: auto;

    &_top {
        margin-top: 0;
    }

    @media screen and (max-width: @phone) {
        width: 100%;
        max-width: 400px;
    }
  }

  &__header {
    margin-bottom: 60px;
  }

  &_visual-constructor {
    height: auto;
    min-height: 100vh;

    .login {
      &__content {
        height: auto;

        &_top {
          height: 100%;
        }
      }
    }

    .info-panel {
      background: #E3F0FF;
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      @media screen and (max-width: @phone) {
        height: auto;
      }

      &-wrapper {
        height: auto;
        width: 1106px;
        margin-right: @spacing-xxl;
        padding-right: 0;

        @media screen and (max-width: 1680px) {
          margin-right: 20px;
        }

        @media screen and (max-width: @tablet) {
          width: auto;
          margin-right: @spacing-m;
          margin-left: @spacing-m;
        }

        @media screen and (max-width: @phone) {
          margin-right: 0;
          margin-left: 0;
        }
      }

      &__content {
        position: relative;
        margin-right: @spacing-xxl;

        @media screen and (max-width: 1300px) {
          margin-right: 34px;
          margin-left: 36px;
        }

        @media screen and (max-width: @phone) {
          margin-top: 36px;
          margin-right: 0;
          margin-left: 0;
        }
      }

      &__title {
        .text(700, @font-size-h1, 56px);

        @media screen and (max-width: 1300px) {
            .text(700, 30px, 110%);
        }

        @media screen and (max-width: @tablet) {
            .text(700, 32px, 110%);
        }

        @media screen and (max-width: @phone) {
          font-size: 22px;
          width: 284px;
          white-space: normal;
        }
      }

      &__description {
        .text(400, 22px, 140%);
        color: #161616;
        white-space: pre-line;
        margin-top: @spacing-xs;
        margin-bottom: @spacing-m;

        @media screen and (min-width: @tablet + 1) and (max-width: 1300px) {
          width: 460px;
          font-size: @font-size-h3;
          white-space: normal;
        }

        @media screen and (max-width: @phone) {
          font-size: @font-size-regular;
          white-space: normal;
          width: 284px;
          text-align: center;
        }
      }

      &__button {
        height: 46px;
        margin-top: 0;
        background: #0067FE;
        border-color: #0067FE;

        :global(.omnica-button__text) {
          color: white;
        }

        &:hover,
        &:active {
          background: #0046CB;
          border-color: #0046CB;
        }
      }

      &__image-wrapper {
        overflow: hidden;
        flex: 1 1 0px;
        display: flex;
        justify-content: flex-start;
        width: 100%;
        margin-top: -115px;

        @media screen and (max-width: @tablet) {
          overflow: visible;
        }

        @media screen and (max-width: @phone) {
          margin-top: 0;
        }
      }

      &__image {
        margin: auto 0 0 0;
        width: 100%;
        max-height: 746px;
        transform: translateY(0);
        min-height: unset;
      }
    }
  }
}

.top {
    display: flex;
    align-items: flex-start;
    width: 100%
}

@media screen and (max-width: @tablet) {
  .login {
    display: block;

    &__image {
      display: none;
    }

    &__content {
      padding: @spacing-s !important;
      margin: auto;
      background: none !important;

      &_retailcrm {
        height: auto;
      }
    }

    &__form-wrap {
      margin: auto;
    }
  }
}

.info-panel {
  width: 100%;
  background: #E6EFF9;
  border-radius: 36px;
  overflow: hidden;

  @media screen and (max-width: @phone) {
    height: 512px;
    border-radius: 0;
  }

  &-wrapper {
    display: flex;
    position: relative;
    height: 100%;
    width: 916px;
    min-height: fit-content;
    padding: @spacing-xxl 64px @spacing-xxl 0;

    @media screen and (min-width: @tablet) and (max-width: 1680px) {
      padding-right: 20px;
    }

    @media screen and (max-width: @tablet) {
      display: flex;
      width: auto;
      height: auto;
      padding: @spacing-l 0 0 0;
      margin: 0 @spacing-m 0 @spacing-m;
    }

    @media screen and (max-width: @phone) {
      width: 100%;
      border-radius: 0;
      margin: 0;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    color: #161616;
    font-family: @font-family;
    z-index: 10000;
    margin-top: 56px;
    margin-left: @spacing-xxl;
    position: absolute;

    @media screen and (max-width: @tablet) {
      margin-top: 36px;
      margin-left: 36px;
    }

    @media screen and (max-width: @phone) {
      align-items: center;
      transform: translateX(-50%);
      margin-top: @spacing-s;
      margin-left: 0;
      left: 50%;
    }
  }

  &__image-wrapper {
    display: contents;
  }

  &__image {
    width: 100%;
    height: 100%;
    min-height: 358px;
    object-fit: cover;
    object-position: 30% top;
    border-radius: 36px;
    position: relative;

    @media screen and (max-width: @phone) {
      border-radius: 0;
    }

    @media screen and (orientation: landscape) and (max-height: 930px) and (max-width: 1200px) {
      transform: translateY(15%);
    }

    @media screen and (max-width: 660px) {
      transform: translateY(15%);
    }
  }

  &__title {
    font-family: @font-family-Gilroy;
    .text(700, 48px, 110%);
    white-space: pre-line;

    @media screen and (min-width: @tablet) and (max-width: 1680px) {
      font-size: 36px;
    }

    @media screen and (max-width: @tablet) {
      font-size: 32px;
    }

    @media screen and (max-width: @phone) {
      font-size: @font-size-h2;
      text-align: center;
      width: max-content;
    }
  }

  &__price {
    display: flex;
    gap: @spacing-xs;
    margin-top: @spacing-s;
    white-space: nowrap;
    .text(700, 48px, 110%);
    font-family: @font-family;

    &-new {
      font-weight: 600;
      color: #15C055;
    }

    &-crossed {
      font-weight: 400;
      color: #AFB9C3;

      &::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: 20%;
        width: 100%;
        height: 2px;
        background-color: currentColor;
        transform: rotate(-10deg);
        transform-origin: bottom left;
      }
    }

    @media screen and (max-width: @phone) {
      font-size: 32px;
    }
  }

  &__button {
    border-color: #161616;
    margin-top: 20px;

    :global(.omnica-button__text) {
      color: #161616;
      transition: color 0.25s ease 0s;
    }

    &:hover,
    &:active {
      background: #161616;

      :global(.omnica-button__text) {
        color: white;
      }
    }

    @media screen and (max-width: @phone) {
      height: @spacing-xl;
      margin-top: @spacing-s;
    }
  }
}
</style>

<i18n locale="ru_RU" lang="json">
{
  "title": "Вход в {siteName}",
  "backToTheSystem": "Вернуться в систему",
  "logout": "Выйти",
  "infoPanel": {
    "title": {
        "text": "7 каналов продаж\nв одной системе",
        "price": "10 400 ₽",
        "priceCrossed": "22 599 ₽"
    },
    "learnMore": "Узнать подробнее"
  },
  "visualConstructor": {
    "infoPanel": {
      "title": "Визуальный конструктор\nмаркетинговых сценариев\nс AI-ассистентом",
      "description": "Запускайте персонализированные рассылки в\u00A0каналах,\nкоторыми пользуются ваши клиенты: от\u00A0email до мессенджеров"
    }
  }
}
</i18n>

<i18n locale="en_GB" lang="json">
{
  "title": "Log in {siteName}",
  "backToTheSystem": "Back to system",
  "logout": "Sign out",
  "infoPanel": {
    "title": {
        "text": "7 sales channels\nin one system",
        "price": "10,400 ₽",
        "priceCrossed": "22,599 ₽"
    },
    "learnMore": "Learn more"
  },
  "visualConstructor": {
    "infoPanel": {
      "title": "Visual builder of marketing scenarios with AI assistant",
      "description": "Send personalized campaigns across channels your clients use: from email to messengers"
    }
  }
}
</i18n>

<i18n locale="es_ES" lang="json">
{
  "title": "Entrar a {siteName}",
  "backToTheSystem": "Volver al sistema",
  "logout": "Cerrar sesión",
  "infoPanel": {
    "title": {
      "text": "7 canales de venta\nen un solo sistema",
      "price": "10 400 ₽",
      "priceCrossed": "22 599 ₽"
    },
    "learnMore": "Saber más"
  },
  "visualConstructor": {
    "infoPanel": {
      "title": "Constructor visual de escenarios de marketing con asistente de IA",
      "description": "Envía campañas personalizadas en los canales que utilizan tus clientes: desde correo electrónico hasta mensajeros"
    }
  }
}
</i18n>
